import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import AddIcon from "@material-ui/icons/Add";
import { useFormik } from "formik";
import { useHistory, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import Loader from "../../shared/Loader";
import { useAppContext } from "../../../api/AppContext";
import {
  createHrDocument,
  createHrDocumentVersion,
  getHrDocument,
  getHrDocumentsVisibilityByUser,
  getHrDocumentVisibilityPreview,
  listHrDocumentCategories,
  listHrDocumentVersions,
  requestHrDocumentsUploadUrl,
  updateHrDocument,
} from "../../../api";
import HrDocumentConditions from "./HrDocumentConditions";

const emptyConditions = {
  id: undefined,
  entity: "",
  contractTypes: [],
  employeeTypes: [],
  employeeRoles: [],
  officeTypes: [],
  officeLocations: [],
  forUserIds: [],
  notForUserIds: [],
};

const emptyText = { title: "", description: "" };

// welfare.idiom rows currently supported for HR documents
const ITALIAN_IDIOM_ID = "8bc337d3-b15a-43f4-b403-99c1c0e45c1a";
const ENGLISH_IDIOM_ID = "c0563f0f-6d39-4e8a-93a7-4076b5582267";
const IDIOMS = [
  { id: ITALIAN_IDIOM_ID, value: "it", displayName: "Italian" },
  { id: ENGLISH_IDIOM_ID, value: "en", displayName: "English" },
];

const getIdiomLabel = (idiomId) =>
  IDIOMS.find((i) => i.id === idiomId)?.displayName || "Unknown language";

// Entities selector returns idiomId plus idiomValue / idiomDisplayName
// (the DB driver may lowercase the aliases).
const resolveEntityIdiomId = (entity) => {
  if (!entity) return null;
  const direct = entity.idiomId || entity.idiomid || null;
  if (direct) return direct;
  const value = String(entity.idiomValue ?? entity.idiomvalue ?? "")
    .trim()
    .toLowerCase();
  const name = String(entity.idiomDisplayName ?? entity.idiomdisplayname ?? "")
    .trim()
    .toLowerCase();
  if (value.startsWith("en") || name.includes("english")) {
    return ENGLISH_IDIOM_ID;
  }
  if (value.startsWith("it") || name.includes("ital")) {
    return ITALIAN_IDIOM_ID;
  }
  return ITALIAN_IDIOM_ID;
};

const getEntityIdiomLabel = (entity) =>
  entity?.idiomDisplayName ||
  entity?.idiomdisplayname ||
  getIdiomLabel(resolveEntityIdiomId(entity));

const ACCEPTED =
  ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.odt,.ods,.odp,application/pdf";
const MAX_UPLOAD_HEADER_BYTES = 512;

const bufferToHex = (buffer) =>
  Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

const toBase64Header = async (file) => {
  const slice = file.slice(0, MAX_UPLOAD_HEADER_BYTES);
  const arrayBuffer = await slice.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
};

const sha256HexOfFile = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", arrayBuffer);
  return bufferToHex(digest);
};

const scanStatusLabel = (status) => {
  if (!status || status === "pending" || status === "in_progress") {
    return `scan: ${status || "pending"}`;
  }
  if (status === "CLEAN") return "scan: ok";
  return `scan: issue (${status})`;
};

let slotCounter = 0;
const newSlot = (props = {}) => {
  slotCounter += 1;
  return {
    key: `slot-${slotCounter}`,
    idiomId: null,
    existingFile: null, // { id, idiomId, currentVersion, ... } from the API
    pending: null, // File chosen locally
    ...props,
  };
};

const slotsFromDocument = (doc) =>
  (doc?.files || []).map((f) =>
    newSlot({ idiomId: f.idiomId, existingFile: f })
  );

const HrDocumentEdit = () => {
  const { id } = useParams();
  const isNew = id === "new";
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { welfareSelectors, fetchWelfareSelector, fetchAngeliniUsers } =
    useAppContext();

  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [versions, setVersions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [slots, setSlots] = useState([]);
  const [docMainIdiomId, setDocMainIdiomId] = useState(null);
  const [internalNameTouched, setInternalNameTouched] = useState(false);
  const [visibilityPreview, setVisibilityPreview] = useState(null);
  const [visibilityByUser, setVisibilityByUser] = useState(null);
  const [simulateUserQuery, setSimulateUserQuery] = useState("");
  const [simulateBusy, setSimulateBusy] = useState(false);
  const fileInputRef = useRef(null);
  const fileTargetSlotRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      textDefault: { ...emptyText },
      textEn: { ...emptyText },
      internalName: "",
      categoryId: "",
      isUserAccessible: false,
      isPublished: false,
      conditions: { ...emptyConditions },
    },
    onSubmit: () => {},
  });

  useEffect(() => {
    [
      "entities",
      "contractType",
      "employeeRole",
      "officeType",
      "employeeType",
      "officeLocation",
    ].forEach((key) => fetchWelfareSelector(key));
  }, [fetchWelfareSelector]);

  useEffect(() => {
    let cancelled = false;
    listHrDocumentCategories()
      .then((rows) => {
        if (!cancelled) setCategories(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        enqueueSnackbar(err?.message || "Failed to load categories", {
          variant: "error",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar]);

  const loadDocument = useCallback(async () => {
    if (isNew) return;
    setIsLoading(true);
    try {
      const doc = await getHrDocument(id);
      const defaultTitle = doc.textDefault?.title || doc.customName || "";
      formik.setValues({
        textDefault: {
          title: defaultTitle,
          description: doc.textDefault?.description ?? doc.description ?? "",
        },
        textEn: {
          title: doc.textEn?.title || "",
          description: doc.textEn?.description || "",
        },
        internalName: doc.internalName || defaultTitle,
        categoryId: doc.categoryId || doc.category?.id || "",
        isUserAccessible: !!doc.isUserAccessible,
        isPublished: !!doc.isPublished,
        conditions: {
          ...emptyConditions,
          ...(doc.conditions || {}),
        },
      });
      setInternalNameTouched(
        !!doc.internalName && doc.internalName !== defaultTitle
      );
      setSlots(slotsFromDocument(doc));
      setDocMainIdiomId(
        (doc.files || []).find((f) => f.isMainIdiom)?.idiomId || null
      );
      const vers = await listHrDocumentVersions(id);
      setVersions(Array.isArray(vers) ? vers : []);
    } catch (err) {
      enqueueSnackbar(err?.message || "Failed to load document", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueSnackbar, id, isNew]);

  useEffect(() => {
    loadDocument();
  }, [loadDocument]);

  const selectedEntity = useMemo(
    () =>
      (welfareSelectors.entities || []).find(
        (e) => e.id === formik.values.conditions?.entity
      ) || null,
    [welfareSelectors.entities, formik.values.conditions?.entity]
  );

  // Main idiom comes from the selected entity; fall back to the one reported by
  // the API for already-saved documents until selectors are loaded.
  const mainIdiomId = selectedEntity
    ? resolveEntityIdiomId(selectedEntity)
    : docMainIdiomId;

  // Always keep a slot for the main idiom once we know it
  useEffect(() => {
    if (!mainIdiomId) return;
    setSlots((prev) =>
      prev.some((s) => s.idiomId === mainIdiomId)
        ? prev
        : [newSlot({ idiomId: mainIdiomId }), ...prev]
    );
  }, [mainIdiomId, slots]);

  const orderedSlots = useMemo(
    () =>
      [...slots].sort((a, b) => {
        if (a.idiomId === mainIdiomId) return -1;
        if (b.idiomId === mainIdiomId) return 1;
        return 0;
      }),
    [slots, mainIdiomId]
  );

  const usedIdiomIds = useMemo(
    () => slots.map((s) => s.idiomId).filter(Boolean),
    [slots]
  );
  const canAddLanguage = IDIOMS.some((i) => !usedIdiomIds.includes(i.id));

  const handleDefaultTitleChange = useCallback(
    (value) => {
      formik.setFieldValue("textDefault.title", value);
      if (!internalNameTouched) {
        formik.setFieldValue("internalName", value);
      }
    },
    [formik, internalNameTouched]
  );

  const addLanguageSlot = useCallback(() => {
    const free = IDIOMS.find((i) => !usedIdiomIds.includes(i.id));
    if (!free) return;
    setSlots((prev) => [...prev, newSlot({ idiomId: free.id })]);
  }, [usedIdiomIds]);

  const changeSlotIdiom = useCallback((slotKey, idiomId) => {
    setSlots((prev) =>
      prev.map((s) => (s.key === slotKey ? { ...s, idiomId } : s))
    );
  }, []);

  const removeSlot = useCallback((slotKey) => {
    setSlots((prev) => prev.filter((s) => s.key !== slotKey));
  }, []);

  const clearPending = useCallback((slotKey) => {
    setSlots((prev) =>
      prev.map((s) => (s.key === slotKey ? { ...s, pending: null } : s))
    );
  }, []);

  const openFileSelector = useCallback((slotKey) => {
    fileTargetSlotRef.current = slotKey;
    fileInputRef.current?.click();
  }, []);

  const onFileSelected = useCallback((event) => {
    const file = event.target.files?.[0];
    const slotKey = fileTargetSlotRef.current;
    // Allow re-selecting the same file later
    event.target.value = "";
    if (!file || !slotKey) return;
    setSlots((prev) =>
      prev.map((s) => (s.key === slotKey ? { ...s, pending: file } : s))
    );
  }, []);

  const validate = useCallback(() => {
    const { textDefault, conditions } = formik.values;
    if (!textDefault.title?.trim()) {
      return "Default language title is required";
    }
    if (!conditions?.entity) {
      return "Entity is required";
    }
    const mainSlot = slots.find((s) => s.idiomId === mainIdiomId);
    const hasMainFile = !!(mainSlot?.pending || mainSlot?.existingFile);
    if (!hasMainFile) {
      return `A file in the entity's main language (${getEntityIdiomLabel(
        selectedEntity
      )}) is required`;
    }
    return null;
  }, [formik.values, mainIdiomId, selectedEntity, slots]);

  const buildDocumentPayload = useCallback(() => {
    const v = formik.values;
    const title = v.textDefault.title.trim();
    return {
      textDefault: {
        title,
        description: v.textDefault.description || "",
      },
      textEn: {
        title: v.textEn.title?.trim() || title,
        description: v.textEn.description || v.textDefault.description || "",
      },
      internalName: v.internalName?.trim() || title,
      categoryId: v.categoryId || null,
      isUserAccessible: v.isUserAccessible,
      isPublished: v.isPublished,
      conditions: v.conditions,
    };
  }, [formik.values]);

  /**
   * Request upload URLs for every pending slot in one call, register a CRM
   * version for each, then PUT the binaries.
   */
  const uploadPendingSlots = useCallback(
    async (documentId) => {
      const pendingSlots = slots.filter((s) => s.pending);
      if (!pendingSlots.length) return { usedStub: false };

      const filesMeta = await Promise.all(
        pendingSlots.map(async (slot) => {
          const file = slot.pending;
          const [fileHeaderBase64, contentHash] = await Promise.all([
            toBase64Header(file),
            sha256HexOfFile(file),
          ]);
          return {
            documentId,
            ...(slot.existingFile?.id && { fileId: slot.existingFile.id }),
            idiomId: slot.idiomId,
            originalFilename: file.name,
            contentType: file.type || "application/octet-stream",
            byteSize: file.size,
            contentHash,
            fileHeaderBase64,
          };
        })
      );

      const uploadResp = await requestHrDocumentsUploadUrl({
        files: filesMeta,
      });
      const uploads = uploadResp?.uploads || [];

      for (let i = 0; i < pendingSlots.length; i += 1) {
        const slot = pendingSlots[i];
        const meta = filesMeta[i];
        const upload = uploads[i];
        if (!upload?.s3Key) {
          throw new Error(`Upload URL missing for ${slot.pending.name}`);
        }
        // Create CRM version before PUT so async scan callback can update scan_status.
        // Prefer upload.fileId so the S3 key path matches the DB file row id
        // (upload-url pre-allocates fileId for new language slots).
        await createHrDocumentVersion(documentId, {
          s3Key: upload.s3Key,
          contentHash: meta.contentHash,
          originalFilename: slot.pending.name,
          contentType: upload.contentType || meta.contentType,
          byteSize: slot.pending.size,
          fileId: upload.fileId || slot.existingFile?.id || undefined,
          idiomId: slot.idiomId || undefined,
        });

        if (upload.uploadUrl) {
          const putRes = await fetch(upload.uploadUrl, {
            method: "PUT",
            headers: upload.headers || {
              "Content-Type": meta.contentType,
              "x-amz-meta-content-hash": meta.contentHash,
            },
            body: slot.pending,
          });
          if (!putRes.ok) {
            throw new Error(
              `Upload failed for ${slot.pending.name} (${putRes.status})`
            );
          }
        }
      }

      return { usedStub: !!uploadResp?.stub };
    },
    [mainIdiomId, slots]
  );

  const save = useCallback(async () => {
    const validationError = validate();
    if (validationError) {
      enqueueSnackbar(validationError, { variant: "warning" });
      return;
    }

    setIsSaving(true);
    let createdId = null;
    try {
      if (isNew) {
        // Create CRM doc first so the S3 key documentId matches the CRM id.
        const created = await createHrDocument(buildDocumentPayload());
        createdId = created?.id || created?.body?.id;
        if (!createdId) {
          throw new Error("Document create did not return an id");
        }
        const { usedStub } = await uploadPendingSlots(createdId);
        enqueueSnackbar(
          usedStub
            ? "Document saved (upload stub — binaries pending Angelini bucket)"
            : "Document created",
          { variant: "success" }
        );
        history.push("/welfares/hr-documents");
        return;
      }

      await updateHrDocument(id, buildDocumentPayload());
      await uploadPendingSlots(id);
      enqueueSnackbar("Document saved", { variant: "success" });
      await loadDocument();
    } catch (err) {
      enqueueSnackbar(err?.message || "Save failed", { variant: "error" });
      if (createdId) {
        // Document exists already: continue on its edit page to retry the files
        history.replace(`/welfares/hr-documents/${createdId}`);
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    buildDocumentPayload,
    enqueueSnackbar,
    history,
    id,
    isNew,
    loadDocument,
    uploadPendingSlots,
    validate,
  ]);

  const loadVisibilityPreview = useCallback(async () => {
    if (isNew) return;
    try {
      const preview = await getHrDocumentVisibilityPreview(id);
      setVisibilityPreview(preview);
    } catch (err) {
      enqueueSnackbar(err?.message || "Visibility preview failed", {
        variant: "error",
      });
    }
  }, [enqueueSnackbar, id, isNew]);

  const runSimulateUser = useCallback(async () => {
    const q = simulateUserQuery.trim();
    if (q.length < 3) {
      enqueueSnackbar("Enter at least 3 characters to find a user", {
        variant: "warning",
      });
      return;
    }
    setSimulateBusy(true);
    try {
      const results = await fetchAngeliniUsers({ searchtext: q });
      const users = Array.isArray(results)
        ? results
        : results?.enabledUsers || results?.users || [];
      const picked = users[0];
      if (!picked?.id) {
        enqueueSnackbar("No user found", { variant: "warning" });
        setVisibilityByUser(null);
        return;
      }
      const visibility = await getHrDocumentsVisibilityByUser({
        userId: picked.id,
        givenname: picked.givenname,
        surname: picked.surname,
        mail: picked.mail,
        entity: picked.entity,
        contracttype: picked.contracttype,
        employeetype: picked.employeetype,
        employeerole: picked.employeerole,
        officelocation: picked.officelocation,
        officetype: picked.officetype,
      });
      setVisibilityByUser(visibility);
    } catch (err) {
      enqueueSnackbar(err?.message || "Simulate user failed", {
        variant: "error",
      });
    } finally {
      setSimulateBusy(false);
    }
  }, [enqueueSnackbar, fetchAngeliniUsers, simulateUserQuery]);

  const selectorsReady = useMemo(
    () =>
      (welfareSelectors.entities || []).length > 0 ||
      (welfareSelectors.contractType || []).length > 0,
    [welfareSelectors]
  );

  const versionLabel = useCallback(
    (v) => {
      const slot = slots.find((s) => s.existingFile?.id === v.hrdocumentFileId);
      const idiom = slot ? getIdiomLabel(slot.idiomId) : null;
      return `${idiom ? `[${idiom}] ` : ""}v${v.versionNumber}${
        v.isCurrent ? " (current)" : ""
      } — ${v.originalFilename || v.s3Key}`;
    },
    [slots]
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box p={3}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Button
            color="primary"
            onClick={() => history.push("/welfares/hr-documents")}
          >
            ← Back to list
          </Button>
          <Typography variant="h5">
            {isNew ? "Add HR document" : "Edit HR document"}
          </Typography>
        </Grid>

        <Grid item md={7} xs={12}>
          <Paper style={{ padding: 16 }}>
            <Typography variant="subtitle1">Default language</Typography>
            <TextField
              fullWidth
              required
              label="Title"
              margin="normal"
              value={formik.values.textDefault.title}
              variant="outlined"
              onChange={(e) => handleDefaultTitleChange(e.target.value)}
            />
            <TextField
              fullWidth
              multiline
              label="Description (optional)"
              margin="normal"
              rows={2}
              value={formik.values.textDefault.description}
              variant="outlined"
              onChange={(e) =>
                formik.setFieldValue("textDefault.description", e.target.value)
              }
            />

            <Box mt={2}>
              <Typography variant="subtitle1">English</Typography>
              <TextField
                fullWidth
                helperText="Falls back to the default language title when empty"
                label="Title"
                margin="normal"
                value={formik.values.textEn.title}
                variant="outlined"
                onChange={(e) =>
                  formik.setFieldValue("textEn.title", e.target.value)
                }
              />
              <TextField
                fullWidth
                multiline
                label="Description (optional)"
                margin="normal"
                rows={2}
                value={formik.values.textEn.description}
                variant="outlined"
                onChange={(e) =>
                  formik.setFieldValue("textEn.description", e.target.value)
                }
              />
            </Box>

            <Divider style={{ margin: "16px 0" }} />

            <TextField
              fullWidth
              helperText="Internal reference only; defaults to the default language title"
              label="Internal name"
              margin="normal"
              value={formik.values.internalName}
              variant="outlined"
              onChange={(e) => {
                setInternalNameTouched(true);
                formik.setFieldValue("internalName", e.target.value);
              }}
            />
            <TextField
              fullWidth
              select
              InputLabelProps={{ shrink: true }}
              label="Category"
              margin="normal"
              value={formik.values.categoryId || ""}
              variant="outlined"
              onChange={(e) =>
                formik.setFieldValue("categoryId", e.target.value)
              }
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.isPublished}
                  onChange={(e) =>
                    formik.setFieldValue("isPublished", e.target.checked)
                  }
                />
              }
              label="Published (available to the agent when conditions match)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.isUserAccessible}
                  onChange={(e) =>
                    formik.setFieldValue("isUserAccessible", e.target.checked)
                  }
                />
              }
              label="User accessible (users may download / list; otherwise agent-only)"
            />

            {selectorsReady && (
              <Box mt={2}>
                <HrDocumentConditions
                  formik={formik}
                  welfareSelectors={welfareSelectors}
                />
              </Box>
            )}

            <Divider style={{ margin: "16px 0" }} />

            <Box mb={2}>
              <Typography variant="subtitle1">Files by language</Typography>
              <Typography color="textSecondary" variant="body2">
                {selectedEntity
                  ? `A file in the entity's main language (${getEntityIdiomLabel(
                      selectedEntity
                    )}) is required. Other languages are optional.`
                  : "Select an entity to see the required main language."}
              </Typography>
              <input
                ref={fileInputRef}
                accept={ACCEPTED}
                style={{ display: "none" }}
                type="file"
                onChange={onFileSelected}
              />

              {orderedSlots.map((slot) => {
                const isMain = slot.idiomId === mainIdiomId;
                const version = slot.existingFile?.currentVersion;
                const hasFile = !!slot.existingFile;
                const takenByOthers = slots
                  .filter((s) => s.key !== slot.key)
                  .map((s) => s.idiomId);
                return (
                  <Box
                    key={slot.key}
                    alignItems="center"
                    display="flex"
                    flexWrap="wrap"
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 4,
                      marginTop: 12,
                      padding: 12,
                      gap: 12,
                    }}
                  >
                    <TextField
                      select
                      disabled={isMain || hasFile}
                      label={isMain ? "Language (main)" : "Language"}
                      size="small"
                      style={{ minWidth: 170 }}
                      value={slot.idiomId || ""}
                      variant="outlined"
                      onChange={(e) =>
                        changeSlotIdiom(slot.key, e.target.value)
                      }
                    >
                      {IDIOMS.filter(
                        (i) =>
                          i.id === slot.idiomId || !takenByOthers.includes(i.id)
                      ).map((i) => (
                        <MenuItem key={i.id} value={i.id}>
                          {i.displayName}
                        </MenuItem>
                      ))}
                    </TextField>

                    <Box flexGrow={1} style={{ minWidth: 180 }}>
                      {slot.pending ? (
                        <Typography variant="body2">
                          {slot.pending.name}{" "}
                          <Chip
                            color="primary"
                            label="to upload on save"
                            size="small"
                          />
                        </Typography>
                      ) : version ? (
                        <>
                          <Typography variant="body2">
                            v{version.versionNumber} —{" "}
                            {version.originalFilename || version.s3Key}
                          </Typography>
                          <Typography color="textSecondary" variant="caption">
                            {scanStatusLabel(version.scanStatus)} · ingest:{" "}
                            {version.ingestStatus || "—"}
                          </Typography>
                        </>
                      ) : (
                        <Typography color="textSecondary" variant="body2">
                          {isMain ? "No file yet (required)" : "No file yet"}
                        </Typography>
                      )}
                    </Box>

                    <Button
                      color="primary"
                      size="small"
                      startIcon={<CloudUploadIcon />}
                      variant="outlined"
                      onClick={() => openFileSelector(slot.key)}
                    >
                      {hasFile || slot.pending ? "Replace" : "Upload"}
                    </Button>
                    {slot.pending && (
                      <Button
                        size="small"
                        onClick={() => clearPending(slot.key)}
                      >
                        Cancel
                      </Button>
                    )}
                    {!isMain && !hasFile && (
                      <Tooltip title="Remove language">
                        <IconButton
                          aria-label="Remove language"
                          size="small"
                          onClick={() => removeSlot(slot.key)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}

              <Box mt={1}>
                <Button
                  color="primary"
                  disabled={!canAddLanguage}
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addLanguageSlot}
                >
                  Add language
                </Button>
              </Box>
              <Typography
                color="textSecondary"
                style={{ display: "block", marginTop: 4 }}
                variant="caption"
              >
                PDF, Word, OpenDocument, PowerPoint, Excel
              </Typography>
            </Box>

            <Box mt={3}>
              <Button
                color="primary"
                disabled={isSaving}
                variant="contained"
                onClick={save}
              >
                {isSaving ? "Saving…" : isNew ? "Create & upload" : "Save"}
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item md={5} xs={12}>
          {!isNew && (
            <Paper style={{ padding: 16, marginBottom: 16 }}>
              <Typography variant="h6">Versions</Typography>
              <List dense>
                {versions.map((v) => (
                  <ListItem key={v.id}>
                    <ListItemText
                      primary={versionLabel(v)}
                      secondary={`${scanStatusLabel(v.scanStatus)} · ingest: ${
                        v.ingestStatus
                      } · ${
                        v.createdAt
                          ? new Date(v.createdAt).toLocaleString()
                          : ""
                      }`}
                    />
                  </ListItem>
                ))}
                {!versions.length && (
                  <Typography color="textSecondary" variant="body2">
                    No versions yet.
                  </Typography>
                )}
              </List>
            </Paper>
          )}

          {!isNew && (
            <Paper style={{ padding: 16, marginBottom: 16 }}>
              <Typography variant="h6">Visibility preview</Typography>
              <Typography color="textSecondary" variant="body2">
                Who this document is associated with (condition summary).
              </Typography>
              <Box mb={1} mt={1}>
                <Button
                  color="primary"
                  size="small"
                  variant="outlined"
                  onClick={loadVisibilityPreview}
                >
                  Refresh audience summary
                </Button>
              </Box>
              {visibilityPreview?.audienceSummary && (
                <Box>
                  <Typography variant="body2">
                    Entity:{" "}
                    {visibilityPreview.audienceSummary.entity?.displayname ||
                      "—"}
                  </Typography>
                  <Typography variant="body2">
                    For users:{" "}
                    {visibilityPreview.audienceSummary.forUserCount || 0} · Not
                    for users:{" "}
                    {visibilityPreview.audienceSummary.notForUserCount || 0}
                  </Typography>
                  <Typography variant="body2">
                    Contract types:{" "}
                    {(visibilityPreview.audienceSummary.contractTypes || [])
                      .map((x) => x.displayname)
                      .join(", ") || "—"}
                  </Typography>
                  <Typography variant="body2">
                    Roles:{" "}
                    {(visibilityPreview.audienceSummary.employeeRoles || [])
                      .map((x) => x.displayname)
                      .join(", ") || "—"}
                  </Typography>
                </Box>
              )}
              <Divider style={{ margin: "16px 0" }} />
              <Typography variant="subtitle1">
                Documents visible for a user
              </Typography>
              <TextField
                fullWidth
                label="Search user (name or email)"
                margin="dense"
                value={simulateUserQuery}
                variant="outlined"
                onChange={(e) => setSimulateUserQuery(e.target.value)}
              />
              <Button
                color="primary"
                disabled={simulateBusy}
                size="small"
                variant="outlined"
                onClick={runSimulateUser}
              >
                {simulateBusy ? "Checking…" : "Simulate user"}
              </Button>
              {visibilityByUser && (
                <Box mt={2}>
                  <Typography variant="body2">
                    User: {visibilityByUser.user?.givenname}{" "}
                    {visibilityByUser.user?.surname} (
                    {visibilityByUser.user?.mail})
                  </Typography>
                  <Typography variant="subtitle2">Visible</Typography>
                  <List dense>
                    {(visibilityByUser.visible || []).map((d) => (
                      <ListItem key={d.documentId}>
                        <ListItemText
                          primary={
                            d.textDefault?.title ||
                            d.internalName ||
                            d.customName
                          }
                          secondary={
                            d.isUserAccessible
                              ? "user accessible"
                              : "agent only"
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Typography variant="subtitle2">Not visible</Typography>
                  <List dense>
                    {(visibilityByUser.notVisible || []).map((d) => (
                      <ListItem key={d.documentId}>
                        <ListItemText
                          primary={
                            d.textDefault?.title ||
                            d.internalName ||
                            d.customName
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default HrDocumentEdit;
